import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import FadeInFromLeft from '../components/Welcome/FadeInFromLeft';
import firebaseAuth from '../services/firebaseAuth';

const colors = {
  mainBlue: "#061941",
  xanthous: "#F2AF29",
  isabelline: "#F4F3EE",
  carrieban: "#3C6E71",
  cafeNoir: "#583E23",
};

export default function Login() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const handleChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    try {
      const result = await firebaseAuth.signInWithGoogle();
      const { user, token, isNewUser } = result;
      
      // Store user data in localStorage
      localStorage.setItem('user', JSON.stringify({ ...user, token }));
      localStorage.setItem('token', token);
      
      if (isNewUser) {
        // If it's a new user, redirect to role selection
        navigate('/role-selection');
      } else {
        // Existing user, redirect to appropriate dashboard
        if (user.role === 'student') {
          navigate('/student-welcome');
        } else if (user.role === 'admin') {
          navigate('/admin');
        } else {
          navigate('/professor-welcome');
        }
      }
    } catch (error) {
      console.error('Google sign in error:', error);
      alert('❌ Google sign in failed. Please try again.');
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    try {
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, formData);
      const { token, user } = res.data;
      localStorage.setItem('user', JSON.stringify({ ...user, token }));
      localStorage.setItem('token', token); 
      // alert('✅ Login successful!');
      if (user.role === 'student') {
        navigate('/student-welcome');
      } else if (user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/professor-welcome');
      }
    } catch (err) {
      // Check if email verification is required
      if (err.response?.status === 403 && err.response?.data?.requiresVerification) {
        // Redirect to email verification
        navigate('/email-verification', { 
          state: { 
            email: formData.email,
            message: err.response.data.message 
          } 
        });
      } else {
        // Show error message
        const errorMessage = err.response?.data?.message || '❌ Login failed.';
        alert(errorMessage);
      }
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotPasswordEmail) {
      alert('❌ Please enter your email address.');
      return;
    }

    setForgotPasswordLoading(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, {
        email: forgotPasswordEmail
      });
      alert('✅ ' + res.data.message);
      setShowForgotPassword(false);
      setForgotPasswordEmail('');
    } catch (err) {
      alert('❌ Failed to send reset link. Please try again.');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        minWidth: "100vw",
        background: `linear-gradient(120deg, ${colors.mainBlue} 70%, ${colors.xanthous} 92%, ${colors.xanthous} 100%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "Segoe UI, Arial, sans-serif"
      }}
    >
      <FadeInFromLeft delay={200}>
        <div
          style={{
            width: "100%",
            maxWidth: "420px",
            background: colors.isabelline,
            borderRadius: "16px",
            boxShadow: `0 4px 24px 0 ${colors.cafeNoir}22`,
            padding: "2.25rem 2rem 2rem 2rem",
            margin: "2rem",
            display: "flex",
            flexDirection: "column",
          }}
        >
        <h2
          style={{
            fontSize: "2rem",
            fontWeight: 700,
            fontFamily: "Poppins, Segoe UI, Arial, sans-serif",
            textAlign: "center",
            color: colors.mainBlue,
            marginBottom: "1.7rem",
            letterSpacing: "1px",
          }}
        >
          Log In
        </h2>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
          <div>
            <label
              htmlFor="email"
              style={{ display: "block", fontWeight: 600, color: colors.carrieban, marginBottom: ".5rem", fontSize: ".98rem" }}
            >
              Email
            </label>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              autoComplete="username"
              style={{
                width: "100%",
                padding: "0.9rem",
                border: `1.7px solid ${colors.carrieban}99`,
                borderRadius: "10px",
                fontSize: "1rem",
                fontFamily: "inherit",
                background: "#fff",
                color: colors.mainBlue,
                outline: "none",
                transition: "border 0.13s"
              }}
              onFocus={e => (e.target.style.border = `2px solid ${colors.xanthous}`)}
              onBlur={e => (e.target.style.border = `1.7px solid ${colors.carrieban}99`)}
            />
          </div>

          <div>
            <label
              htmlFor="password"
              style={{ display: "block", fontWeight: 600, color: colors.carrieban, marginBottom: ".5rem", fontSize: ".98rem" }}
            >
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              required
              autoComplete="current-password"
              style={{
                width: "100%",
                padding: "0.9rem",
                border: `1.7px solid ${colors.carrieban}99`,
                borderRadius: "10px",
                fontSize: "1rem",
                fontFamily: "inherit",
                background: "#fff",
                color: colors.mainBlue,
                outline: "none",
                transition: "border 0.13s"
              }}
              onFocus={e => (e.target.style.border = `2px solid ${colors.xanthous}`)}
              onBlur={e => (e.target.style.border = `1.7px solid ${colors.carrieban}99`)}
            />
          </div>

          <button
            type="submit"
            style={{
              width: "100%",
              marginTop: "1.5rem",
              background: colors.xanthous,
              color: colors.mainBlue,
              padding: "1rem 0",
              borderRadius: "10px",
              fontWeight: 700,
              fontSize: "1.15rem",
              border: "none",
              cursor: "pointer",
              transition: "background 0.12s, color 0.12s",
              boxShadow: `0 2px 8px 0 ${colors.xanthous}33`
            }}
            aria-label="Log In"
          >
            Log In
          </button>

          {/* Forgot Password Link */}
          <div style={{ textAlign: "center", marginTop: "1rem" }}>
            <button
              type="button"
              onClick={() => navigate('/forgot-password')}
              style={{
                color: colors.carrieban,
                textDecoration: "none",
                fontSize: "0.9rem",
                fontWeight: 500,
                transition: "color 0.2s",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0
              }}
              onMouseOver={(e) => e.target.style.color = colors.xanthous}
              onMouseOut={(e) => e.target.style.color = colors.carrieban}
            >
              Forgot your password?
            </button>
          </div>

          {/* Email Verification Link */}
          <div style={{ textAlign: "center", marginTop: "0.5rem" }}>
            <button
              type="button"
              onClick={() => navigate('/email-verification')}
              style={{
                color: colors.carrieban,
                textDecoration: "none",
                fontSize: "0.9rem",
                fontWeight: 500,
                transition: "color 0.2s",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0
              }}
              onMouseOver={(e) => e.target.style.color = colors.xanthous}
              onMouseOut={(e) => e.target.style.color = colors.carrieban}
            >
              Need to verify your email?
            </button>
          </div>

          {/* Divider */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            margin: '0.75rem 0',
            color: colors.carrieban
          }}>
            <div style={{ flex: 1, height: '1px', background: colors.carrieban + '40' }}></div>
            <span style={{ margin: '0 1rem', fontSize: '0.9rem', fontWeight: 500 }}>or</span>
            <div style={{ flex: 1, height: '1px', background: colors.carrieban + '40' }}></div>
          </div>

          {/* Google Sign In Button */}
          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={googleLoading}
            style={{
              width: "100%",
              background: "#fff",
              color: colors.mainBlue,
              padding: "1rem 0",
              borderRadius: "10px",
              fontWeight: 600,
              fontSize: "1rem",
              border: `2px solid ${colors.carrieban}40`,
              cursor: googleLoading ? "not-allowed" : "pointer",
              transition: "all 0.2s",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
              boxShadow: "0 2px 8px 0 rgba(0,0,0,0.1)"
            }}
            onMouseOver={(e) => {
              if (!googleLoading) {
                e.target.style.border = `2px solid ${colors.xanthous}`;
                e.target.style.background = colors.xanthous + "10";
              }
            }}
            onMouseOut={(e) => {
              if (!googleLoading) {
                e.target.style.border = `2px solid ${colors.carrieban}40`;
                e.target.style.background = "#fff";
              }
            }}
          >
            {googleLoading ? (
              <>
                <div style={{
                  width: "20px",
                  height: "20px",
                  border: `2px solid ${colors.carrieban}40`,
                  borderTop: `2px solid ${colors.xanthous}`,
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite"
                }}></div>
                Signing in...
              </>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </>
            )}
          </button>
        </form>
        </div>
      </FadeInFromLeft>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "1rem"
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowForgotPassword(false);
              setForgotPasswordEmail('');
            }
          }}
        >
          <div
            style={{
              background: colors.isabelline,
              borderRadius: "16px",
              padding: "2rem",
              maxWidth: "400px",
              width: "100%",
              boxShadow: `0 4px 24px 0 ${colors.cafeNoir}22`,
            }}
          >
            <h3
              style={{
                fontSize: "1.5rem",
                fontWeight: 700,
                color: colors.mainBlue,
                marginBottom: "1rem",
                textAlign: "center"
              }}
            >
              Reset Password
            </h3>
            
            <p
              style={{
                color: colors.carrieban,
                marginBottom: "1.5rem",
                textAlign: "center",
                fontSize: "0.95rem"
              }}
            >
              Enter your email address and we'll send you a link to reset your password.
            </p>

            <form onSubmit={handleForgotPassword}>
              <div style={{ marginBottom: "1.5rem" }}>
                <label
                  htmlFor="forgot-email"
                  style={{ 
                    display: "block", 
                    fontWeight: 600, 
                    color: colors.carrieban, 
                    marginBottom: ".5rem", 
                    fontSize: ".98rem" 
                  }}
                >
                  Email Address
                </label>
                <input
                  id="forgot-email"
                  type="email"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  required
                  style={{
                    width: "100%",
                    padding: "0.9rem",
                    border: `1.7px solid ${colors.carrieban}99`,
                    borderRadius: "10px",
                    fontSize: "1rem",
                    fontFamily: "inherit",
                    background: "#fff",
                    color: colors.mainBlue,
                    outline: "none",
                    transition: "border 0.13s"
                  }}
                  onFocus={e => (e.target.style.border = `2px solid ${colors.xanthous}`)}
                  onBlur={e => (e.target.style.border = `1.7px solid ${colors.carrieban}99`)}
                />
              </div>

              <div style={{ display: "flex", gap: "1rem" }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotPasswordEmail('');
                  }}
                  style={{
                    flex: 1,
                    padding: "0.8rem",
                    border: `2px solid ${colors.carrieban}`,
                    borderRadius: "10px",
                    background: "transparent",
                    color: colors.carrieban,
                    fontWeight: 600,
                    cursor: "pointer",
                    transition: "all 0.2s"
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = colors.carrieban;
                    e.target.style.color = colors.isabelline;
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = "transparent";
                    e.target.style.color = colors.carrieban;
                  }}
                >
                  Cancel
                </button>
                
                <button
                  type="submit"
                  disabled={forgotPasswordLoading}
                  style={{
                    flex: 1,
                    padding: "0.8rem",
                    background: forgotPasswordLoading ? "#ccc" : colors.xanthous,
                    color: colors.mainBlue,
                    border: "none",
                    borderRadius: "10px",
                    fontWeight: 600,
                    cursor: forgotPasswordLoading ? "not-allowed" : "pointer",
                    transition: "background 0.2s"
                  }}
                >
                  {forgotPasswordLoading ? "Sending..." : "Send Reset Link"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* CSS for spinner animation */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}